import { useState } from "react";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Alert, Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { getItems } from "../../../api/items";
import { useUser } from "../../../api/useAuth";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { useDashboardAccess } from "../../../dashboard-framework/DashboardAccessProvider";
import { DashboardDateFilter } from "../../../dashboard-framework/DashboardDateFilter";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { resolveDateFilterRange } from "../../../dashboard-framework/datePresets";
import { formatQuantity } from "../../../dashboard-framework/format";
import { DrillDownConfig, GlobalFilterValues } from "../../../dashboard-framework/types";
import { combineFilters } from "../../../dashboard-framework/useAggregate";
import { DataTableCard } from "../../../dashboard-framework/widgets/DataTableCard";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { TimeSeriesChart } from "../../../dashboard-framework/widgets/TimeSeriesChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { fetchAllItemPages } from "../shared/fetchAllItemPages";
import {
    DATE_MODE_OPTIONS,
    describeDoneTotalPt,
    describeFunctionCardsPt,
    describeNonActiveGroupCardPt,
    describeOpenTotalPt,
    describeTablePt,
    describeUnassignedCardPt,
    describeWeeklyChartPt,
    DONE_TASKS_FIELDS,
    functionOverlapNotePt,
    doneTasksFilter,
    FUNCTION_OPTIONS,
    OPEN_TASKS_FIELDS,
    openTasksFilter,
    PEOPLE_FIELDS,
    PEOPLE_LOOKUP_CHUNK_SIZE,
    STATUS_FILTER_OPTIONS,
    TASKS_DRILL_FIELDS,
    TaskDateModeKey,
    TaskStatusFilterKey,
    taskViewerFilter,
    TEAM_TASKS_PERIOD_PRESETS,
    TEAM_TASKS_RED_OPEN_THRESHOLD,
    TeamFunctionFilterKey,
    withOwnViewNotePt,
} from "./teamTasksConfig";
import {
    PersonInfo,
    stripTaskHtml,
    teamTasksFunctionCards,
    teamTasksGlobalCards,
    teamTasksSpecialGroupTotals,
    teamTasksTableRows,
    TeamTasksTableRow,
    teamTasksWeeklyPoints,
} from "./teamTasksRows";

const FIVE_MINUTES = 5 * 60 * 1000;

// Universos acima disto (15 páginas × 1000) disparam o aviso de período demasiado longo.
const TASKS_MAX_PAGES = 15;

const formatDayPt = (isoDate: string | null | undefined) => {
    if (!isoDate) return null;
    const formatted = format(parseISO(isoDate), "dd/MM/yyyy");
    return formatted;
};

// A rota /users ignora limit/page (createRoute) e responde sempre com ≤100 registos —
// o lookup é feito em blocos de ids para nunca depender de paginação.
const fetchPeopleByIds = async (personIds: Array<string>) => {
    const idChunks: Array<Array<string>> = [];
    for (let chunkStart = 0; chunkStart < personIds.length; chunkStart += PEOPLE_LOOKUP_CHUNK_SIZE) {
        idChunks.push(personIds.slice(chunkStart, chunkStart + PEOPLE_LOOKUP_CHUNK_SIZE));
    }
    const chunkResults = await Promise.all(
        idChunks.map((idChunk) => getItems("directus_users", PEOPLE_FIELDS, { filter: { id: { _in: idChunk } }, sort: "first_name" }))
    );
    const people = chunkResults.flatMap((chunkResult) => chunkResult.items);
    return people;
};

const RED_ROW_BACKGROUND = "#FDECEC";
const RED_TEXT_COLOR = "#B3261E";

const initialsOf = (displayName: string) => {
    const nameParts = displayName.split(" ").filter(Boolean);
    const initials = nameParts
        .slice(0, 2)
        .map((namePart) => namePart[0])
        .join("")
        .toUpperCase();
    return initials;
};

type PersonNameCellProps = {
    row: TeamTasksTableRow;
};

const PersonNameCell = ({ row }: PersonNameCellProps) => {
    if (row.isUnassigned || row.isNonActiveGroup) {
        return (
            <Typography variant="body2" fontStyle="italic" color="text.secondary">
                {row.displayName}
            </Typography>
        );
    }

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Box
                width={28}
                height={28}
                borderRadius="50%"
                bgcolor="#E6F1FB"
                color="#185FA5"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                fontSize={11}
                fontWeight={600}
                flexShrink={0}
            >
                {initialsOf(row.displayName)}
            </Box>
            <Typography variant="body2" fontWeight={600}>
                {row.displayName}
            </Typography>
            {row.isRed && <WarningAmberRoundedIcon fontSize="small" sx={{ color: RED_TEXT_COLOR }} />}
        </Box>
    );
};

type GroupCountsContentProps = {
    openCount: number;
    doneCount: number;
    showDone: boolean;
};

const GroupCountsContent = ({ openCount, doneCount, showDone }: GroupCountsContentProps) => {
    return (
        <Box>
            <Typography variant="h6" component="span" fontWeight={600}>
                {formatQuantity(openCount)}
            </Typography>
            <Typography variant="body2" component="span" color="text.secondary">
                {" "}
                em aberto
            </Typography>
            {showDone && (
                <Typography variant="body2" color="text.secondary">
                    <Typography variant="subtitle1" component="span" fontWeight={600} color={DASHBOARD_COLORS.doneDark}>
                        {formatQuantity(doneCount)}
                    </Typography>{" "}
                    feitas
                </Typography>
            )}
        </Box>
    );
};

const assigneeNames = (task: any) => {
    const names = (task.assigned_users || [])
        .map((assignee: any) => assignee?.directus_users_id)
        .filter(Boolean)
        .map((user: any) => (typeof user === "object" ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : String(user)))
        .filter(Boolean);
    const label = names.length > 0 ? names.join(", ") : "—";
    return label;
};

const tasksDrillColumns = [
    {
        key: "status",
        label: "Estado",
        sortField: "status",
        valueFor: (task: any) => (task.status === "done" ? "Concluída" : "Aberta"),
    },
    { key: "assigned_users", label: "Atribuída a", valueFor: (task: any) => assigneeNames(task) },
    { key: "content", label: "Descrição", valueFor: (task: any) => stripTaskHtml(task.content).slice(0, 180) },
    { key: "due_date", label: "Prazo", sortField: "due_date", valueFor: (task: any) => formatDayPt(task.due_date) },
    { key: "date_created", label: "Criada", sortField: "date_created", valueFor: (task: any) => formatDayPt(task.date_created) },
    { key: "done_date", label: "Concluída", sortField: "done_date", valueFor: (task: any) => formatDayPt(task.done_date) },
];

const taskRoute = (task: any) => (task.id ? `/tasks/${task.id}/detail` : null);

const tasksDrillConfig = (title: string, taskIds: Array<number>, queryDescription: string): DrillDownConfig => {
    const config: DrillDownConfig = {
        kind: "query",
        title,
        entity: "tasks",
        fieldsQuery: TASKS_DRILL_FIELDS,
        filter: { id: { _in: taskIds } },
        defaultSort: "-date_created",
        columns: tasksDrillColumns,
        rowRoute: taskRoute,
        queryDescription,
    };
    return config;
};

type FilterSelectProps<OptionKey extends string> = {
    label: string;
    value: OptionKey;
    options: Array<{ key: OptionKey; label: string }>;
    onChange: (value: OptionKey) => void;
    disabled?: boolean;
};

const FilterSelect = <OptionKey extends string>({ label, value, options, onChange, disabled }: FilterSelectProps<OptionKey>) => {
    return (
        <TextField
            select
            size="small"
            label={label}
            value={value}
            onChange={(event) => onChange(event.target.value as OptionKey)}
            disabled={disabled}
            sx={{ minWidth: 180 }}
        >
            {options.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
};

export const TeamTasksDashboard = () => {
    const access = useDashboardAccess();
    const userResult = useUser();
    const [functionFilter, setFunctionFilter] = useState<TeamFunctionFilterKey>("todas");
    const [dateMode, setDateMode] = useState<TaskDateModeKey>("conclusao");
    const [periodValues, setPeriodValues] = useState<GlobalFilterValues>({ period: "ultimos_30_dias" });
    const [chartPersonKey, setChartPersonKey] = useState("all");
    const setPeriodValue = (filterKey: string, value: string | number | null) => {
        setPeriodValues((currentValues) => ({ ...currentValues, [filterKey]: value }));
    };

    // Vista pessoal (viewer_rules → params.taskUserId): tudo filtrado às tarefas do próprio.
    // Enquanto o $current_user não resolve, as queries ficam parqueadas (nunca mostrar tudo).
    const currentUserId: string | null = userResult.data?.id || null;
    const viewerParamValue = access.viewerParams?.taskUserId;
    const taskUserId = viewerParamValue === undefined ? null : viewerParamValue === "$current_user" ? currentUserId : String(viewerParamValue);
    const isOwnView = viewerParamValue !== undefined;
    const viewerPending = isOwnView && taskUserId === null;

    const now = new Date();
    // sem_periodo (default) resolve para null → vista de stock (feitas nem são fetchadas).
    const periodRange = resolveDateFilterRange(periodValues, "period", now);
    const hasPeriod = periodRange !== null;

    // O filtro de Estado foi removido: a tabela mostra sempre os colaboradores com tarefas em
    // aberto (a coluna "Feitas no período" continua a aparecer quando há período). Sem período as
    // feitas nem são fetchadas — todo o histórico (36k+) não é um universo limitável nem útil.
    const effectiveStatusFilter: TaskStatusFilterKey = "abertas";

    const viewerClause = taskUserId ? taskViewerFilter(taskUserId) : undefined;
    const openFilter = combineFilters(openTasksFilter(dateMode, periodRange), viewerClause);
    const doneFilter = periodRange ? combineFilters(doneTasksFilter(dateMode, periodRange), viewerClause) : null;

    const openResult = useQuery({
        queryKey: ["team-tasks-open", JSON.stringify(openFilter)],
        queryFn: () => fetchAllItemPages("tasks", OPEN_TASKS_FIELDS, openFilter, { maxPages: TASKS_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled: !viewerPending,
    });
    const doneResult = useQuery({
        queryKey: ["team-tasks-done", JSON.stringify(doneFilter)],
        queryFn: () => fetchAllItemPages("tasks", DONE_TASKS_FIELDS, doneFilter, { maxPages: TASKS_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled: hasPeriod && !viewerPending,
    });

    const openTasks = openResult.data?.items || [];
    const doneTasks = hasPeriod ? doneResult.data?.items || [] : [];
    const reachedPageLimit = Boolean(openResult.data?.reachedPageLimit || (hasPeriod && doneResult.data?.reachedPageLimit));

    const allAssigneeIds = Array.from(
        new Set(
            [...openTasks, ...doneTasks].flatMap((task: any) =>
                (task.assigned_users || [])
                    .map((assigneeRef: any) => assigneeRef?.directus_users_id)
                    .filter((assigneeId: any) => Boolean(assigneeId))
            )
        )
    ).sort();
    // Na vista pessoal só interessa o próprio (co-atribuídos das tarefas partilhadas não se mostram).
    const assigneeIds = taskUserId ? [taskUserId] : allAssigneeIds;

    const peopleResult = useQuery({
        queryKey: ["team-tasks-people-list", assigneeIds.join(",")],
        queryFn: () => fetchPeopleByIds(assigneeIds),
        enabled: assigneeIds.length > 0,
        staleTime: FIVE_MINUTES,
    });
    const peopleById: Record<string, PersonInfo> = {};
    const people: Array<PersonInfo> = Array.isArray(peopleResult.data) ? peopleResult.data : [];
    people.forEach((person) => {
        peopleById[person.id] = person;
    });

    const inputs = { openTasks, doneTasks, peopleById };
    const effectiveFunctionFilter: TeamFunctionFilterKey = isOwnView ? "todas" : functionFilter;
    const allTableRows = teamTasksTableRows(inputs, effectiveStatusFilter, effectiveFunctionFilter, TEAM_TASKS_RED_OPEN_THRESHOLD);
    const tableRows = taskUserId ? allTableRows.filter((row) => row.personKey === taskUserId) : allTableRows;
    const globalCards = teamTasksGlobalCards(inputs, effectiveFunctionFilter, TEAM_TASKS_RED_OPEN_THRESHOLD);
    const functionCards = teamTasksFunctionCards(inputs, effectiveFunctionFilter);
    const specialGroups = teamTasksSpecialGroupTotals(inputs);

    // Reconciliação: funções + inativos + sem responsável == total global; a sobreposição
    // (tarefas em 2+ funções) é o excedente da soma dos cartões — mostrada com o número exato.
    const functionCardsOpenSum = functionCards.reduce((sum, card) => sum + card.openCount, 0);
    const functionOverlapCount =
        effectiveFunctionFilter === "todas"
            ? functionCardsOpenSum + specialGroups.nonActive.openCount + specialGroups.unassigned.openCount - globalCards.openTotal
            : 0;
    // Com "Todas" há 6 cartões de grupo (4 funções + Inativos + Sem responsável) — md={2} para
    // caberem numa linha; com uma função filtrada há só 1 cartão, que fica melhor mais largo.
    const groupCardSpanMd = effectiveFunctionFilter === "todas" ? 2 : 3;
    const weeklyPersonKey = taskUserId || chartPersonKey;
    const weeklyPoints = periodRange ? teamTasksWeeklyPoints(inputs, effectiveFunctionFilter, weeklyPersonKey, dateMode, periodRange) : [];
    const chartPoints = weeklyPoints.map((point) => ({ monthLabel: point.weekLabel, feitas: point.doneCount }));

    const isLoading =
        viewerPending || openResult.isLoading || (hasPeriod && doneResult.isLoading) || (assigneeIds.length > 0 && peopleResult.isLoading);
    const isError = openResult.isError || (hasPeriod && doneResult.isError) || peopleResult.isError;

    const chartPersonOptions = tableRows
        .filter((row) => !row.isUnassigned && !row.isNonActiveGroup)
        .map((row) => ({ key: row.personKey, label: row.displayName }))
        .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label, "pt"));
    const chartPersonLabel = chartPersonOptions.find((option) => option.key === weeklyPersonKey)?.label || null;

    // Drill da linha (modal padrão "Visualizar dados subjacentes"): ids capturados no render a
    // partir das MESMAS contagens da tabela — zero drift por construção; gate por drill_roles
    // vem do DrillDownProvider (contexto null ⇒ linhas não clicáveis).
    const tableRowDrillDown = (rowIndex: number): DrillDownConfig | null => {
        const row = tableRows[rowIndex];
        if (!row) return null;
        const drillTaskIds =
            effectiveStatusFilter === "abertas"
                ? row.openTaskIds
                : effectiveStatusFilter === "feitas"
                  ? row.doneTaskIds
                  : [...row.openTaskIds, ...row.doneTaskIds];
        if (drillTaskIds.length === 0) return null;
        const statusLabel = STATUS_FILTER_OPTIONS.find((option) => option.key === effectiveStatusFilter)?.label || effectiveStatusFilter;
        const config = tasksDrillConfig(
            `Tarefas (${statusLabel}) — ${row.displayName}`,
            drillTaskIds,
            `As mesmas tarefas que originam as contagens da linha de ${row.displayName} na vista atual (estado "${statusLabel}").`
        );
        return config;
    };

    const buildGroupDrill = (label: string, openTaskIds: Array<number>, doneTaskIds: Array<number>): DrillDownConfig | undefined => {
        const groupTaskIds = [...openTaskIds, ...doneTaskIds];
        if (groupTaskIds.length === 0) return undefined;
        const periodNote = hasPeriod ? " e feitas no período" : "";
        const config = tasksDrillConfig(`${label} — tarefas`, groupTaskIds, `Tarefas de ${label} na vista atual (abertas${periodNote}).`);
        return config;
    };

    const tableColumns = [
        { key: "person", label: "Colaborador" },
        { key: "functions", label: "Função" },
        { key: "open", label: "Em aberto", isNumeric: true },
        ...(hasPeriod ? [{ key: "done", label: "Feitas no período", isNumeric: true }] : []),
    ];

    const tableDisplayRows = tableRows.map((row) => {
        const displayRow: Record<string, React.ReactNode> = {
            person: <PersonNameCell row={row} />,
            functions: row.functionLabels.join(" · ") || "—",
            open: (
                <Typography variant="body2" component="span" fontWeight={600} color={row.isRed ? RED_TEXT_COLOR : undefined}>
                    {row.openCount}
                </Typography>
            ),
            done: row.doneCount,
        };
        return displayRow;
    });

    const tableRowSx = (rowIndex: number) => {
        const rowSx = tableRows[rowIndex]?.isRed ? { backgroundColor: RED_ROW_BACKGROUND } : undefined;
        return rowSx;
    };

    return (
        <DashboardPage globalFilters={[]} values={{}} onChange={() => undefined}>
            <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
                {!isOwnView && (
                    <FilterSelect
                        label="Função"
                        value={functionFilter}
                        options={[{ key: "todas" as TeamFunctionFilterKey, label: "Todas" }, ...FUNCTION_OPTIONS]}
                        onChange={setFunctionFilter}
                    />
                )}
                <FilterSelect label="Tipo de data" value={dateMode} options={DATE_MODE_OPTIONS} onChange={setDateMode} disabled={!hasPeriod} />
                <DashboardDateFilter
                    label="Período"
                    filterKey="period"
                    values={periodValues}
                    onChange={setPeriodValue}
                    presets={TEAM_TASKS_PERIOD_PRESETS}
                />
            </Box>

            {reachedPageLimit && (
                <Alert severity="warning">
                    O período escolhido devolve demasiadas tarefas e os números podem estar incompletos — encurta o período.
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <StatCard
                        label="Tarefas em aberto"
                        value={isLoading ? undefined : formatQuantity(globalCards.openTotal)}
                        color={DASHBOARD_COLORS.highlight}
                        explanation={{
                            queryDescription: withOwnViewNotePt(describeOpenTotalPt(dateMode, periodRange, effectiveFunctionFilter), isOwnView),
                        }}
                        drillDown={
                            globalCards.openTaskIds.length
                                ? tasksDrillConfig(
                                      "Tarefas em aberto",
                                      globalCards.openTaskIds,
                                      withOwnViewNotePt("As tarefas em aberto que compõem este total, na vista atual.", isOwnView)
                                  )
                                : undefined
                        }
                        isLoading={isLoading}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <StatCard
                        label="Feitas no período"
                        value={!hasPeriod ? "—" : isLoading ? undefined : formatQuantity(globalCards.doneTotal)}
                        color={hasPeriod ? DASHBOARD_COLORS.done : DASHBOARD_COLORS.neutral}
                        explanation={{
                            queryDescription: withOwnViewNotePt(describeDoneTotalPt(dateMode, periodRange, effectiveFunctionFilter), isOwnView),
                        }}
                        drillDown={
                            globalCards.doneTaskIds.length
                                ? tasksDrillConfig(
                                      "Feitas no período",
                                      globalCards.doneTaskIds,
                                      withOwnViewNotePt("As tarefas concluídas contadas neste período, na vista atual.", isOwnView)
                                  )
                                : undefined
                        }
                        isLoading={hasPeriod && isLoading}
                    />
                </Grid>
            </Grid>

            {!isOwnView && (
                <Box>
                    <Grid container spacing={3}>
                        {functionCards.map((card) => (
                            <Grid item xs={6} md={groupCardSpanMd} key={card.functionKey}>
                                <WidgetCard
                                    title={card.functionLabel}
                                    explanation={{ queryDescription: describeFunctionCardsPt(dateMode, periodRange) }}
                                    drillDown={buildGroupDrill(card.functionLabel, card.openTaskIds, card.doneTaskIds)}
                                    isLoading={isLoading}
                                    isError={isError}
                                >
                                    <GroupCountsContent openCount={card.openCount} doneCount={card.doneCount} showDone={hasPeriod} />
                                </WidgetCard>
                            </Grid>
                        ))}
                        {effectiveFunctionFilter === "todas" && (
                            <>
                                <Grid item xs={6} md={groupCardSpanMd}>
                                    <WidgetCard
                                        title="Inativos / sem identificação"
                                        explanation={{ queryDescription: describeNonActiveGroupCardPt() }}
                                        drillDown={buildGroupDrill(
                                            "Inativos / sem identificação",
                                            specialGroups.nonActive.openTaskIds,
                                            specialGroups.nonActive.doneTaskIds
                                        )}
                                        isLoading={isLoading}
                                        isError={isError}
                                    >
                                        <GroupCountsContent
                                            openCount={specialGroups.nonActive.openCount}
                                            doneCount={specialGroups.nonActive.doneCount}
                                            showDone={hasPeriod}
                                        />
                                    </WidgetCard>
                                </Grid>
                                <Grid item xs={6} md={groupCardSpanMd}>
                                    <WidgetCard
                                        title="Sem responsável"
                                        explanation={{ queryDescription: describeUnassignedCardPt() }}
                                        drillDown={buildGroupDrill(
                                            "Sem responsável",
                                            specialGroups.unassigned.openTaskIds,
                                            specialGroups.unassigned.doneTaskIds
                                        )}
                                        isLoading={isLoading}
                                        isError={isError}
                                    >
                                        <GroupCountsContent
                                            openCount={specialGroups.unassigned.openCount}
                                            doneCount={specialGroups.unassigned.doneCount}
                                            showDone={hasPeriod}
                                        />
                                    </WidgetCard>
                                </Grid>
                            </>
                        )}
                    </Grid>
                    {functionOverlapCount > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            {functionOverlapNotePt(functionOverlapCount)}
                        </Typography>
                    )}
                </Box>
            )}

            <DataTableCard
                title={isOwnView ? "As tuas tarefas" : "Tarefas por colaborador"}
                explanation={{ queryDescription: withOwnViewNotePt(describeTablePt(effectiveStatusFilter, dateMode, periodRange), isOwnView) }}
                columns={tableColumns}
                rows={tableDisplayRows}
                totalsRow={{
                    person: "Total (tarefas distintas)",
                    open: formatQuantity(globalCards.openTotal),
                    done: hasPeriod ? formatQuantity(globalCards.doneTotal) : undefined,
                }}
                maxBodyHeight={560}
                getRowSx={tableRowSx}
                rowDrillDown={tableRowDrillDown}
                isLoading={isLoading}
                isError={isError}
            />

            <WidgetCard
                title="Tarefas feitas por semana"
                explanation={{
                    queryDescription: periodRange
                        ? withOwnViewNotePt(
                              describeWeeklyChartPt(dateMode, periodRange, weeklyPersonKey === "all" ? null : chartPersonLabel),
                              isOwnView
                          )
                        : "Sem período escolhido, as tarefas feitas não são contadas. Escolhe um período no filtro para veres a evolução semanal.",
                }}
                headerControls={
                    hasPeriod && !isOwnView ? (
                        <FilterSelect
                            label="Colaborador(a)"
                            value={chartPersonKey}
                            options={[{ key: "all", label: "Toda a equipa" }, ...chartPersonOptions]}
                            onChange={setChartPersonKey}
                        />
                    ) : undefined
                }
                isLoading={hasPeriod && isLoading}
                isError={isError}
            >
                {hasPeriod ? (
                    <TimeSeriesChart
                        points={chartPoints}
                        series={[{ key: "feitas", label: "Tarefas feitas", color: DASHBOARD_COLORS.done }]}
                        height={320}
                    />
                ) : (
                    <Box py={6} textAlign="center">
                        <Typography variant="body2" color="text.secondary">
                            Escolhe um período no filtro para veres as tarefas feitas por semana.
                        </Typography>
                    </Box>
                )}
            </WidgetCard>
        </DashboardPage>
    );
};
